import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ComponentFactoryResolver, ComponentRef, Inject, Injectable, Type, ViewContainerRef } from '@angular/core';
import { NavigationExtras, Router, UrlSegment, UrlSegmentGroup, UrlTree } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class BtlHelperService {
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private http: HttpClient,
        private resolver: ComponentFactoryResolver,
        private router: Router,
    ) {}

    /**
     * Formats Firstname by setting first char in uppercase
     * @param str
     * @returns
     */
    public static formatFirstname(str: string): string {
        return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
    }

    /**
     * Formats phone number :
     *  - removes +33
     *  - adds spaces
     * @param str
     * @returns
     */
    public static formatPhoneNumber(str: string): string {
        if (!str) {
            return '';
        }
        const arr: string[] = str.replace('+33', '0').split('');
        for (let i = 2; i < 10; i += 2) {
            arr.splice(i + (arr.length - 10), 0, ' ');
        }
        return arr.join('');
    }

    /**
     * Remove html from a text
     * @param text
     * @param tags
     * @return string
     */
    public static removeHtml(text: string, tags: string[] = ['']): string {
        tags.forEach((tag) => {
            const regex = new RegExp('<' + tag + '[^>]*>', 'g');
            text = text.replace(regex, '');
        });

        return text;
    }

    /**
     * Redirects to external url using window.location
     * @param url
     */
    public navigateToExternalURL(
        url: string,
        replaceHistoryState: boolean = false,
        isComponentRouting: boolean = false,
        state: { [key: string]: string } = {},
    ): void {
        const extras: NavigationExtras = { state };

        if (replaceHistoryState) {
            this.replaceHistoryState(url);
        }
        if (isComponentRouting) {
            const urlTree = this.router.parseUrl(url);
            this.router.navigateByUrl(urlTree, extras);
        } else {
            this.document.defaultView.location.href = url;
        }
    }

    public getAcquisUrlFromRenew(url: string): string {
        return url.replace('/renouvellement', '');
    }

    public replaceHistoryState(url: string) {
        this.document.defaultView.history.replaceState(null, null, url);
    }

    public updateSize() {
        let event = null;
        if (typeof Event === 'function') {
            event = new Event('resize');
        } else {
            // IE
            event = this.document.createEvent('Event'); // Deprecated bbut since IE version are freezed this will always work
            event.initEvent('resize', true, true);
        }
        this.document.defaultView.dispatchEvent(event);
    }

    public uuid(): string {
        return `${Date.now() + Math.random().toString().slice(2)}`;
    }

    /**
     * Tri par attribut
     * @param attr
     * @param a
     * @param b
     * @param numeric
     * @returns
     */
    public static sortByAttribute(attr: string, a: any, b: any, numeric: boolean = false) {
        if (numeric) {
            return Number(a[attr]) - Number(b[attr]);
        } else {
            return a[attr] - b[attr];
        }
    }

    public isEmptyObject(obj: { [key: string]: any } = {}): boolean {
        return Object.keys(obj).length > 0;
    }

    /**
     * Add class on <body>
     * @param clss
     */
    public addBodyClass(clss: string): void {
        this.document.body.classList.add(clss);
    }

    /**
     * Remove class from <body>
     * @param clss
     */
    public removeBodyClass(clss: string): void {
        this.document.body.classList.remove(clss);
    }

    public createComponent<T>(entry: ViewContainerRef, componentType: Type<T>): ComponentRef<T> {
        entry.clear();
        const factory = this.resolver.resolveComponentFactory(componentType);
        const componentRef = entry.createComponent(factory);
        return componentRef;
    }

    /**
     * Get Url without origine
     * @param
     * @returns
     */
    public getUrlPath(absoluteUrl: string): string {
        const tree: UrlTree = this.router.createUrlTree([absoluteUrl]);
        const group: UrlSegmentGroup = tree.root.children.primary;
        const segments: UrlSegment[] = group.segments;
        let url = '';
        for (let i = 2; i < segments.length; i++) {
            url += `/${segments[i].path}`;
        }
        return url;
    }

    /**
     * Formats string for tracking
     * @param str
     * @returns
     */
    public static formatForTracking(str: string): string {
        return str.split(' ').join('_').replace('-', '_').replace(/[éè]/g, 'e');
    }

    /**
     * Rounding number
     * @param {number} value
     * @param {number} numberFix
     * @returns {string}
     */

    public round(value: number, numberFix: number = 2) {
        value /= 1.2;
        return Number.isInteger(value)
            ? `${value}`
            : (Math.round(Number((value * Math.pow(10, numberFix)).toFixed(1))) / Math.pow(10, numberFix)).toFixed(
                  numberFix,
              );
    }
}
